// Audit Log — read-only stream of admin actions (super_admin only).
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Shield, Search } from "lucide-react";
import { adminApi } from "@/lib/adminAuth";

const ACTION_COLOR = {
    login: "bg-jade/15 text-jade",
    logout: "bg-navy/10 text-navy/70",
    change_password: "bg-amber-100 text-amber-800",
    staff_create: "bg-gold/15 text-gold",
    staff_update: "bg-gold/15 text-gold",
    staff_delete: "bg-red-100 text-red-700",
    reservation_create: "bg-jade/15 text-jade",
    reservation_update: "bg-blue-50 text-blue-700",
    reservation_status: "bg-blue-50 text-blue-700",
    reservation_delete: "bg-red-100 text-red-700",
    payment_record: "bg-jade/15 text-jade",
    refund_record: "bg-amber-100 text-amber-800",
    resend_confirmation: "bg-navy/10 text-navy/70",
    media_upload: "bg-gold/15 text-gold",
    media_delete: "bg-red-100 text-red-700",
    media_update: "bg-blue-50 text-blue-700",
    room_update: "bg-blue-50 text-blue-700",
    room_publish_toggle: "bg-amber-100 text-amber-800",
    block_create: "bg-neutral-800 text-white",
    block_delete: "bg-neutral-300 text-neutral-700",
};

export default function AdminAuditLog() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (q) params.set("actor_email", q);
            const { data } = await adminApi.get(`/admin/audit-log?${params}`);
            setRows(data);
        } catch { toast.error("Could not load audit log"); }
        finally { setLoading(false); }
    };
    useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

    return (
        <div data-testid="admin-audit-page">
            <header className="mb-6">
                <div className="text-[10px] uppercase tracking-[0.28em] text-gold">Security</div>
                <h1 className="font-display text-4xl md:text-5xl text-[#0b1f22] mt-3">Audit log</h1>
                <p className="text-[#0b1f22]/60 mt-2 text-sm">Every administrative action recorded, with the staff member, timestamp, and target. Read-only.</p>
            </header>

            <div className="mb-4 relative max-w-md">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy/40" />
                <input
                    data-testid="audit-search"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Filter by staff email"
                    className="w-full pl-9 pr-3 py-2.5 rounded-full border border-navy/15 text-sm focus:border-gold focus:outline-none bg-white"
                />
            </div>

            {loading ? <div className="text-navy/60 text-sm">Loading audit log…</div> : (
                <div className="bg-white rounded-2xl border border-navy/10 overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-navy/5 text-[10px] uppercase tracking-[0.18em] text-navy/60">
                            <tr>
                                <th className="text-left px-5 py-3">When</th>
                                <th className="text-left px-5 py-3">Who</th>
                                <th className="text-left px-5 py-3">Action</th>
                                <th className="text-left px-5 py-3 hidden md:table-cell">Target</th>
                                <th className="text-left px-5 py-3 hidden lg:table-cell">Details</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.id} data-testid={`audit-row-${r.id}`} className="border-t border-navy/10 align-top">
                                    <td className="px-5 py-3 text-navy/70 whitespace-nowrap">
                                        {new Date(r.created_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                                    </td>
                                    <td className="px-5 py-3">
                                        <div className="text-navy text-sm">{r.actor_email}</div>
                                        <div className="text-[10px] text-navy/50 uppercase tracking-[0.14em] mt-0.5">{r.actor_role?.replace("_", " ")}</div>
                                    </td>
                                    <td className="px-5 py-3">
                                        <span className={`text-[10px] uppercase tracking-[0.16em] px-2.5 py-1 rounded-full ${ACTION_COLOR[r.action] || "bg-navy/10 text-navy/70"}`}>
                                            {r.action.replace(/_/g, " ")}
                                        </span>
                                    </td>
                                    <td className="px-5 py-3 hidden md:table-cell font-mono text-[11px] text-navy/60 truncate max-w-[200px]">{r.target || "—"}</td>
                                    <td className="px-5 py-3 hidden lg:table-cell text-[11px] text-navy/60 max-w-md truncate">
                                        {r.meta && Object.keys(r.meta).length ? JSON.stringify(r.meta) : "—"}
                                    </td>
                                </tr>
                            ))}
                            {rows.length === 0 && (
                                <tr><td colSpan={5} className="px-5 py-10 text-center text-navy/50">
                                    <Shield size={20} className="mx-auto mb-2 text-navy/30" /> No entries yet
                                </td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
